import { GeApi, GeApiState, GlobalServiceInterface } from "../Ge";
import {
    AllRecipientTypes,
    AuditActor,
    AuditEmailRecipient,
    AuditEvent,
    AuditingEmailData,
    AuditPayload,
    PDPDecision,
    PlatformInfo,
    RecipientsByAllTypes,
    UserDecision
} from "../Ge/types";
import { Container, GlobalConfiguration } from "./GlobalConfiguration";
import { OutlookActions, RecipientType } from "./OutlookActions";
import { GeException } from "../infrastructure"
import { AppointMentRecipientType } from "./AppointmentOutlookActions";
import { v4 as uuidv4 } from 'uuid';
import { i18n, TFunction } from "i18next";
import {
    DECISION_OVERRIDE,
    DL_EXPANSION_BUDGET_MS,
    DL_EXPANSION_CONCURRENCY,
    DL_MAX_NESTING_DEPTH,
    DL_MAX_REPORTED_DIAGNOSTICS
} from "./constants";

export interface ResolvedEmails {
    recipientsByType: RecipientsByAllTypes;
    unresolvedLists: string[];
}

export class EmailFields {

    protected globalService: GlobalServiceInterface;
    protected container: Container;
    protected itemType: Office.MailboxEnums.ItemType;
    private _unresolvedLists: string[] = [];
    /**
     * Leaf addresses keyed by Azure AD group id, for one resolveAllEmails() run. A group reached from
     * several parents - or from both the To and the Cc field - is fetched from Graph once.
     */
    private _groupMembersCache: Map<string, string[]> = new Map();
    /** Wall-clock deadline for the whole DL traversal; set by resolveAllEmails(). */
    private _expansionDeadline: number = 0;
    private _expansionBudgetSpent: boolean = false;
    /**
     * Counts branches cut short by a cycle, the depth cap or the time budget. A result computed with a
     * truncation inside it is incomplete, so it must not be cached as if it were the group's membership.
     */
    private _truncations: number = 0;
    /**
     * Every expansion problem hit during one run, newest last. setCustomContext merges into a single
     * global object, so writing a `reason` per incident would leave Kibana holding only the last one.
     * These are collected and reported once, in resolveAllEmails().
     */
    private _expansionDiagnostics: string[] = [];
    /** One limiter for every Graph member fetch in the run, so nesting cannot multiply the width. */
    private _graphPermits: Semaphore = new Semaphore(DL_EXPANSION_CONCURRENCY);

    constructor(globalService: GlobalServiceInterface, container: Container, itemType: Office.MailboxEnums.ItemType) {
        this.globalService = globalService;
        this.container = container;
        this.itemType = itemType;
    }

    private async resolveEmailList(recipientField: AllRecipientTypes): Promise<string[]> {
        let recipientList: Set<string> = new Set();

        const toRecipients = await this.globalService.outlookActions.getRecipients(recipientField);
        for (let idx = 0; idx < toRecipients.length; idx++) {
            const recipient = toRecipients[idx];
            const emailAddress = recipient.emailAddress;

            switch (recipient.recipientType) {
                case Office.MailboxEnums.RecipientType.Other:
                case Office.MailboxEnums.RecipientType.DistributionList: {
                    const dlMembers = await this.resolveDistributionList(emailAddress, recipient.displayName, toRecipients);
                    dlMembers?.forEach((dlMember) => recipientList.add(dlMember));
                } break;

                case Office.MailboxEnums.RecipientType.User:
                case Office.MailboxEnums.RecipientType.ExternalUser:
                default:

                    if (!emailAddress) {
                        this.globalService.apm.setCustomContext({
                            recipientField,
                            recipient,
                            "recipient-not-resolved": true
                        });

                        continue;
                    }

                    this.checkEmailAddressFormat(emailAddress, toRecipients);
                    recipientList.add(emailAddress.toLowerCase());
                    break;
            }
        }
        return Array.from(recipientList);
    }

    /**
     * Expands a Distribution List into its member addresses, following nested lists.
     *
     * Nested expansion is bounded on three axes - depth, cycles and wall-clock time - because none of
     * them are bounded by Azure AD. Once the time budget is spent the traversal stops and the list
     * fails *open*: the DL address is handed to the server in place of its members, so the send still
     * goes through rather than wedging until Outlook reports "Secure Email timed out.".
     *
     * @param dlEmail the email address of the list
     */
    private async resolveDistributionList(dlEmail: string, dlName: string, recipientField?: Office.EmailAddressDetails[]): Promise<string[]> {
        // If there's no email address, this is a personal contact list — skip Graph lookup
        // to avoid accidentally matching an unrelated Azure AD group with the same display name.
        // Only treat strictly empty/blank dlEmail as a CL; non-SMTP identifiers (e.g. legacyExchangeDN)
        // should still attempt Graph resolution.
        if (!dlEmail || !dlEmail.trim()) {
            if (dlName) {
                this._unresolvedLists.push(dlName);

                this.globalService?.apm?.setCustomContext({
                    recipientField,
                    name: dlName,
                    "recipient-not-resolved": true,
                    "reason": "Personal contact list detected (no SMTP address) — sent as unresolved list"
                });
            }
            return [];
        }

        // An earlier list already spent the budget: don't start more Graph work, just pass this one through.
        if (!this.hasExpansionBudget()) {
            return this.passListThroughUnexpanded(dlEmail, dlName, recipientField, 'expansion budget already spent');
        }

        let group: Awaited<ReturnType<typeof this.globalService.outlookActions.findGraphGroup>>;
        try {
            group = await this.globalService.outlookActions.findGraphGroup(dlName, dlEmail, this.expansionBudget());
        } catch (e) {
            // A failed lookup (throttling, a transient Graph error) must not fail the send either.
            this.globalService?.apm?.captureError?.(e instanceof Error ? e : new Error(String(e)));
            return this.passListThroughUnexpanded(dlEmail, dlName, recipientField, `group lookup failed: ${e instanceof Error ? e.message : String(e)}`);
        }

        if (!group?.id) {
            return this.handleUnresolvableList(dlEmail, dlName, recipientField);
        }

        const truncationsBefore = this._truncations;

        // Fast path: let Graph flatten the nesting. Falls back to walking the tree ourselves only for
        // group types that cannot serve transitiveMembers.
        let recipientList = await this.expandGroupTransitively(group.id);

        if (recipientList === null) {
            recipientList = await this.expandGroupById(group.id, recipientField, new Set());
        }

        // Any truncation counts as incomplete, not just a spent budget. A depth cap or a failed nested
        // fetch also leaves members out, and if a sibling branch returned addresses the result looks
        // healthy - so without this the missing recipients would vanish from validation silently.
        if (this._expansionBudgetSpent || this._truncations > truncationsBefore) {
            // Partial membership: keep what was resolved and add the list address so the server still
            // sees the list rather than the send being blocked on an incomplete answer.
            return recipientList.concat(
                this.passListThroughUnexpanded(dlEmail, dlName, recipientField, 'expansion incomplete')
            );
        }

        if (!recipientList.length) {
            // Group exists but resolved to nothing usable - hand the address to the server as before.
            return this.passListThroughUnexpanded(dlEmail, dlName, recipientField, 'group expanded to no addresses');
        }

        return recipientList;
    }

    /**
     * Fail-open path: the list could not be expanded (in time, or at all), so its own address goes to
     * the server. The server validates what it can and the send is never blocked on our inability to
     * enumerate members.
     */
    private passListThroughUnexpanded(
        dlEmail: string,
        dlName: string,
        recipientField: Office.EmailAddressDetails[] | undefined,
        reason: string
    ): string[] {
        // apm.addLabels, not getCurrentTransaction()?.addLabels: the latter silently drops the label
        // if no transaction happens to be current, and this one says a list went out unvalidated.
        this.globalService?.apm?.addLabels({ "dl-expansion-incomplete": true });
        this.noteDiagnostic(`list not expanded (${dlName || dlEmail}): ${reason}`);

        if (!dlEmail.includes('@')) {
            // A non-SMTP identifier (legacyExchangeDN and friends) cannot be handed to the server as an
            // address, so there is nothing to fail open *with*. It is deliberately not pushed to
            // unresolvedLists either: the backend blocks those with no override, and a list we merely
            // ran out of time on must not block a send. It is surfaced instead.
            this.globalService?.apm?.addLabels({ "dl-dropped-non-smtp": true });
            this.noteDiagnostic(`non-SMTP identifier dropped, unvalidated (${dlName || dlEmail}): ${reason}`);

            return [];
        }

        this.checkEmailAddressFormat(dlEmail, recipientField);

        return [dlEmail.toLowerCase()];
    }

    /**
     * Graph has no group matching this recipient at all.
     */
    private handleUnresolvableList(dlEmail: string, dlName: string, recipientField?: Office.EmailAddressDetails[]): string[] {
        if (dlEmail.includes('@')) {
            // Has valid SMTP format — likely a Dynamic Distribution Group or org DL
            // not synced to Azure AD. Pass through to server validation.
            this.checkEmailAddressFormat(dlEmail, recipientField);
            return [dlEmail.toLowerCase()];
        }

        // Non-SMTP identifier (e.g. legacyExchangeDN) that Graph couldn't resolve.
        // Treat as unresolved so backend can block with no override.
        const name = dlName || dlEmail;
        this._unresolvedLists.push(name);
        this.globalService?.apm?.setCustomContext({
            recipientField,
            name,
            "recipient-not-resolved": true,
            "reason": "Non-SMTP identifier could not be resolved via Graph — sent as unresolved list"
        });

        return [];
    }

    /**
     * Expands a group through Graph's transitiveMembers, which returns the nesting already flattened.
     *
     * This is the whole point of the fast path: Graph resolves nested groups - and any circular nesting
     * - server side, so one paginated call replaces a request per nested group. The bounds below still
     * apply to it, but only the time budget can actually bite, since there is no client-side recursion
     * left to run away.
     *
     * @returns null when the caller should walk the tree instead, either because this group type cannot
     *          serve transitiveMembers or because the answer came back empty and only the walk can say
     *          whether the group is genuinely empty.
     */
    private async expandGroupTransitively(groupId: string): Promise<string[] | null> {
        if (!this.hasExpansionBudget()) {
            return [];
        }

        let members: Awaited<ReturnType<typeof this.globalService.outlookActions.getTransitiveGroupMembers>>;

        try {
            members = await this.globalService.outlookActions.getTransitiveGroupMembers(groupId, this.expansionBudget());
        } catch (e) {
            // Deliberately not falling back to the walk here. Whatever made one request fail - throttling,
            // an outage - would only be made worse by replaying it as hundreds of them. Fail open instead:
            // an empty result sends the list address to the server.
            this.noteTruncation(groupId, `transitiveMembers failed: ${e instanceof Error ? e.message : String(e)}`);
            this.globalService?.apm?.captureError?.(e instanceof Error ? e : new Error(String(e)));
            return [];
        }

        if (members === null) {
            // Unsupported for this group type - the walk is the answer.
            return null;
        }

        const recipientList: string[] = [];

        for (const member of members) {
            // Nested groups come back alongside the leaves; the leaves are what we validate.
            if (member["@odata.type"] === "#microsoft.graph.group") {
                continue;
            }

            if (member.mail) {
                recipientList.push(member.mail.toLowerCase());
            } else {
                this.noteDiagnostic(`group ${groupId}: transitive member of type ${member["@odata.type"]} has no email, recipient removed`);
            }
        }

        // Evaluated before the test below, never inside it: hasExpansionBudget() is what *records* a
        // spent budget, and `&&` would skip it whenever members came back - which is precisely when a
        // partially paged list would otherwise be handed on as though it were complete.
        //
        // Paging stops on the same condition this reads, so a truncated page set always lands here with
        // the budget already gone. It can also fire when the budget ran out during a single complete
        // page; calling that incomplete costs one extra address to the server and never lost validation,
        // so it errs the right way.
        const budgetLeft = this.hasExpansionBudget();

        // An empty answer is ambiguous - a genuinely empty group looks the same as one this endpoint
        // silently declines to expand - so let the walk settle it. Costs one request on empty lists.
        // Once the budget is gone there is nothing to settle it with, so take the empty answer as final.
        if (recipientList.length === 0 && budgetLeft) {
            return null;
        }

        return recipientList;
    }

    /**
     * Recursively expands a group by its Azure AD id, resolving all leaf members.
     *
     * The fallback for group types transitiveMembers cannot serve. Nested groups are followed by id
     * rather than by re-resolving them from their mail address: the id is already in the members
     * response, and a mail lookup costs two or three extra Graph round-trips per nested group. Siblings
     * are expanded in parallel, since one-at-a-time traversal of a wide tree is what pushed the send
     * past Outlook's ItemSend limit.
     *
     * @param groupId - Azure AD group id
     * @param recipientField - for APM context
     * @param ancestors - group ids on the path from the top-level list to here, for cycle detection
     */
    private async expandGroupById(
        groupId: string,
        recipientField: Office.EmailAddressDetails[] | undefined,
        ancestors: Set<string>
    ): Promise<string[]> {
        const cached = this._groupMembersCache.get(groupId);
        if (cached) {
            return cached;
        }

        // Azure AD allows a group to contain one of its own ancestors. Following that loops forever.
        if (ancestors.has(groupId)) {
            this.noteTruncation(groupId, 'nested group cycle');
            return [];
        }

        if (ancestors.size >= DL_MAX_NESTING_DEPTH) {
            this.noteTruncation(groupId, `nesting deeper than ${DL_MAX_NESTING_DEPTH} levels`);
            return [];
        }

        if (!this.hasExpansionBudget()) {
            this.noteTruncation(groupId, 'expansion budget spent');
            return [];
        }

        const truncationsBefore = this._truncations;
        // A copy per branch, so concurrently expanded siblings are never mistaken for each other's ancestors.
        const path = new Set(ancestors).add(groupId);

        const recipientList: string[] = [];
        const nestedGroupIds: string[] = [];

        let members: Awaited<ReturnType<typeof this.globalService.outlookActions.getGroupMembers>> | null;
        try {
            // The permit covers the fetch only, never the expansion of the children below it: holding
            // it across the recursion would let parents waiting on children exhaust every permit and
            // deadlock the traversal.
            members = await this._graphPermits.run(() => {
                // Re-checked *after* the permit as well as before it. A branch can sit in the queue
                // until the deadline passes, and budgetedTimeout clamps to 1ms rather than zero, so it
                // would otherwise fire a request certain to fail - noise in Kibana on the one path
                // where the telemetry has to stay readable.
                if (!this.hasExpansionBudget()) {
                    return Promise.resolve(null);
                }

                return this.globalService.outlookActions.getGroupMembers(groupId, this.expansionBudget());
            });
        } catch (e) {
            // One unreachable group must not fail the send. Report it and carry on with what resolved -
            // the caller falls back to handing the list address to the server.
            this.noteTruncation(groupId, `members could not be fetched: ${e instanceof Error ? e.message : String(e)}`);
            this.globalService?.apm?.captureError?.(e instanceof Error ? e : new Error(String(e)));
            return recipientList;
        }

        if (members === null) {
            this.noteTruncation(groupId, 'budget spent while queued for a Graph permit');
            return recipientList;
        }

        // getGroupMembers stops paging when the budget runs out and returns what it has. Nothing below
        // would notice on a group with no nested children, so a leaf's partial membership would look
        // complete - the same hole the fast path had.
        if (!this.hasExpansionBudget()) {
            this.noteTruncation(groupId, 'budget spent while paging members');
        }

        for (const member of members) {
            if (member["@odata.type"] === "#microsoft.graph.group") {
                if (member.id) {
                    nestedGroupIds.push(member.id);
                }
                continue;
            }

            if (member.mail) {
                recipientList.push(member.mail.toLowerCase());
            } else {
                this.noteDiagnostic(`group ${groupId}: member of type ${member["@odata.type"]} has no email, recipient removed`);
            }
        }

        // Not a per-level concurrency limit: nesting would multiply it (ten parents x ten children is a
        // hundred in flight, a thousand at the next level). The single _graphPermits limiter around the
        // fetches is what actually bounds the width, so these can all be started.
        const nested = await Promise.all(
            nestedGroupIds.map((nestedId) => this.expandGroupById(nestedId, recipientField, path))
        );

        for (const addresses of nested) {
            recipientList.push(...addresses);
        }

        // Only a complete answer is worth reusing; a truncated one would leak its missing members into
        // every other list that contains this group.
        //
        // Deliberately *not* an in-flight promise map keyed by group id, which would also spare the
        // duplicate fetch when two concurrent branches reach the same group. That reintroduces the
        // deadlock this code exists to prevent: with two mutually nested groups under one parent, each
        // branch would end up awaiting the other's in-flight promise and neither could finish. One
        // repeated fetch in a diamond is the cheaper trade.
        if (this._truncations === truncationsBefore) {
            this._groupMembersCache.set(groupId, recipientList);
        }

        return recipientList;
    }

    /**
     * True while there is still time to keep expanding. Flips permanently once the budget is spent, so
     * every remaining list short-circuits to the fail-open path instead of each paying its own timeout.
     */
    private hasExpansionBudget(): boolean {
        if (this._expansionBudgetSpent) {
            return false;
        }

        if (this._expansionDeadline && Date.now() >= this._expansionDeadline) {
            this._expansionBudgetSpent = true;
            this.globalService?.apm?.addLabels({ "timed-out:dl-expansion": true });
            this.globalService?.apm?.captureError?.(
                new Error(`Distribution list expansion exceeded its ${DL_EXPANSION_BUDGET_MS}ms budget; remaining lists passed through unexpanded`)
            );
            return false;
        }

        return true;
    }

    /**
     * Ships everything collected during the run, as one context write plus a count label so the cases
     * are findable in Kibana without reading the context of every send.
     */
    private reportExpansionDiagnostics(): void {
        if (this._expansionDiagnostics.length === 0) {
            return;
        }

        // Swallowed deliberately. This runs from a finally, so anything thrown here would replace the
        // error on its way out - and that error is what drives the user-facing message (GeException
        // carries the id the hook reports). Diagnostics must never displace the thing they describe.
        try {
            this.globalService?.apm?.setCustomContext({
                "dl-expansion-issues": this._expansionDiagnostics,
                "dl-expansion-issue-count": this._expansionDiagnostics.length,
                "dl-expansion-truncations": this._truncations
            });
            this.globalService?.apm?.addLabels({ "dl-expansion-issues": this._expansionDiagnostics.length });
        } catch {
            // no-op: telemetry must not break the send path
        }
    }

    /**
     * The traversal's remaining time, for Graph calls to bound their own requests with.
     */
    private expansionBudget(): { remainingMs(): number } {
        return { remainingMs: () => Math.max(0, this._expansionDeadline - Date.now()) };
    }

    private noteTruncation(groupId: string, reason: string): void {
        this._truncations++;
        this.noteDiagnostic(`group ${groupId} truncated: ${reason}`);
    }

    /**
     * Records one expansion problem. Capped, because this ends up in an APM payload that has to be
     * delivered in the window between transaction.end() and Outlook tearing down the webview.
     */
    private noteDiagnostic(detail: string): void {
        if (this._expansionDiagnostics.length < DL_MAX_REPORTED_DIAGNOSTICS) {
            this._expansionDiagnostics.push(detail);
        }
    }

    /**
     * Verifies that the email address is RFC822 compliant (not to the full extend though)
     * @param emailAddress
     */
    private checkEmailAddressFormat(emailAddress: string, recipientField?: Office.EmailAddressDetails[]) {
        if (typeof(emailAddress) !== "string" ) {
            const errorMessage = `Bad email address in recipient field`;
            const errorData = {
                recipientField,
                emailAddress
            };
            const error = new Error(errorMessage);
            (error as any).data = errorData;

            throw(error);
        }
        const atSignIndex = emailAddress.lastIndexOf('@');
        if (atSignIndex == -1) {
            throw new GeException('hook.bad_email_address', `Malformed email address: ${emailAddress}`, { address: emailAddress });
        }
    }

    public async resolveAllEmails(): Promise<ResolvedEmails> {
        this._unresolvedLists = [];
        this._groupMembersCache.clear();
        this._truncations = 0;
        this._expansionDiagnostics = [];
        this._graphPermits = new Semaphore(DL_EXPANSION_CONCURRENCY);
        this._expansionBudgetSpent = false;
        this._expansionDeadline = Date.now() + DL_EXPANSION_BUDGET_MS;

        // Reported in a finally, because resolving can throw past this point and the sends that throw are
        // the ones whose diagnostics are worth most. checkEmailAddressFormat raises on a malformed
        // address from three places inside the loop, and getRecipients can reject - so one bad address
        // in Cc would otherwise discard everything a DL in To had already recorded, leaving
        // dl-expansion-incomplete set in Kibana with nothing saying why.
        try {
            let recipientsByType: RecipientsByAllTypes;
            if (this.itemType === Office.MailboxEnums.ItemType.Appointment) {
                recipientsByType = {
                    [AppointMentRecipientType.OPTIONAL]: await this.resolveEmailList(AppointMentRecipientType.OPTIONAL),
                    [AppointMentRecipientType.REQUIRED]: await this.resolveEmailList(AppointMentRecipientType.REQUIRED)
                };
            } else {
                recipientsByType = {
                    [RecipientType.TO]: await this.resolveEmailList(RecipientType.TO),
                    [RecipientType.CC]: await this.resolveEmailList(RecipientType.CC),
                    [RecipientType.BCC]: await this.resolveEmailList(RecipientType.BCC)
                };
            }

            return {
                recipientsByType,
                unresolvedLists: [...new Set(this._unresolvedLists)]
            };
        } finally {
            this.reportExpansionDiagnostics();
        }
    }
}

/**
 * A counting semaphore: at most `limit` holders run at once, the rest queue.
 *
 * One of these is shared by a whole distribution-list traversal. Limiting concurrency per recursion
 * level instead would multiply down the tree - ten parents each starting ten children is a hundred
 * requests in flight, and a thousand at the level below - which is exactly the throttling the bound
 * exists to avoid.
 */
export class Semaphore {
    private active = 0;
    private waiting: Array<() => void> = [];

    constructor(private readonly limit: number) {}

    public async run<T>(work: () => Promise<T>): Promise<T> {
        // A loop rather than a single await: several waiters can be released before this one resumes.
        while (this.active >= this.limit) {
            await new Promise<void>(resolve => this.waiting.push(resolve));
        }

        this.active++;

        try {
            return await work();
        } finally {
            this.active--;
            this.waiting.shift()?.();
        }
    }
}

export const delay = (ms: number) : Promise<void> => {
    return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * Raised by withTimeout when the wrapped promise does not settle in time. Carries the operation name
 * so the failing call is identifiable from APM without a source map.
 */
export class TimeoutError extends Error {
    public readonly operation: string;
    public readonly timeoutMs: number;

    constructor(operation: string, timeoutMs: number) {
        super(`${operation} timed out after ${timeoutMs}ms`);
        this.name = 'TimeoutError';
        this.operation = operation;
        this.timeoutMs = timeoutMs;
    }
}

/**
 * Records that a bounded call settled after we had already reported it as timed out.
 *
 * Office.js exposes no cancellation API for setAsync/saveAsync/getAsync, so the losing side of the race
 * in withTimeout keeps running to completion. For a mutating call that means the write can still land
 * after we have moved on, leaving our in-memory state and the actual item out of step - saveDraft is
 * the case that matters, since a late save leaves `emailId` unset while the draft does exist.
 *
 * That cannot be prevented, so it is made observable instead: a steady rate here means a budget is too
 * tight, while silence means timeouts really are wedged calls rather than slow ones. Errors are
 * reported outside a transaction, which APM RUM ships (transaction.name is simply absent).
 */
const reportLateSettlement = (operation: string, timeoutMs: number, outcome: 'resolved' | 'rejected') => {
    try {
        OutlookActions.apm?.captureError(
            new Error(`${operation} settled (${outcome}) after its ${timeoutMs}ms timeout was already reported`)
        );
    } catch {
        // diagnostics must never break the send path
    }
};

/**
 * Bounds a promise that may never settle. Office.js host callbacks can fail to fire at all - when that
 * happens inside a synchronous ItemSend handler the send wedges permanently (AB#14110), so every such
 * call on the send path must go through here.
 *
 * This races rather than cancels, because Office.js gives us nothing to cancel with. The underlying
 * call keeps running and its result is discarded; if it later settles, reportLateSettlement records it.
 * Callers that mutate state must therefore treat a timeout as "unknown outcome", not as "did not
 * happen" - the write may still land. HTTP callers should prefer axios's own `timeout`, which does
 * genuinely abort the request.
 */
export const withTimeout = <T>(promise: Promise<T>, ms: number, operation = 'Operation'): Promise<T> => {
    let timer: ReturnType<typeof setTimeout>;
    let timedOut = false;

    const timeout = new Promise<never>((_, reject) => {
        timer = setTimeout(() => {
            timedOut = true;
            reject(new TimeoutError(operation, ms));
        }, ms);
    });

    // Observe the loser of the race. Both branches are handled so this never surfaces as an unhandled
    // rejection, and both are no-ops unless the timeout has already fired.
    promise.then(
        () => { if (timedOut) reportLateSettlement(operation, ms, 'resolved'); },
        () => { if (timedOut) reportLateSettlement(operation, ms, 'rejected'); },
    );

    return Promise.race([promise, timeout]).finally(() => clearTimeout(timer)) as Promise<T>;
}

/**
 * withTimeout for calls that must not propagate a timeout to the caller - either because their own
 * failure mode is to resolve a fallback rather than reject, or because they sit on a recovery path
 * where throwing would replace the original error.
 *
 * The timeout is swallowed for control-flow purposes but always reported: these are the cases where
 * the add-in carries on having skipped something it intended to do, so the only record that it
 * happened is what we emit here. Never make this silent.
 */
export const withTimeoutOr = async <T>(promise: Promise<T>, ms: number, fallback: T, operation = 'Operation'): Promise<T> => {
    try {
        return await withTimeout(promise, ms, operation);
    } catch (e) {
        if (e instanceof TimeoutError) {
            try {
                OutlookActions.apm?.getCurrentTransaction()?.addLabels({ [`timed-out:${operation}`]: true });
                OutlookActions.apm?.captureError(e);
            } catch {
                // diagnostics must never break the send path
            }

            return fallback;
        }
        throw e;
    }
}

export const AuditEvents = {
    PDP_DECISION: 'outlook_pdp_decision',
    USER_DECISION: 'outlook_pep_user_decision',
    EMAIL_SENT: 'outlook_email_sent',
    CANCEL: 'outlook_pep_prompted_cancel',
}

export const constructAuditEventData = (action: string, geApiState: GeApiState): AuditPayload => {
    const platformInfo = JSON.parse(localStorage.getItem("platformInfo"));
    const platformData: PlatformInfo = {
        type: "outlook",
        outlook: {
            platform: platformInfo?.platform,
            version: platformInfo?.version
        }
    }

    const mapRecipientToPayload = (recipient: AuditEmailRecipient): AuditEmailRecipient => {
        return {
            email: recipient.email,
            is_cc: recipient.is_cc,
            is_bcc: recipient.is_bcc,
        }
    };

    const allRecipients = geApiState.allRecipients?.map(mapRecipientToPayload);
    const allowedRecipients = geApiState.allRecipients
        ?.filter((recipient) => recipient.allowed || recipient?.decision === DECISION_OVERRIDE)
        .map(mapRecipientToPayload);

    let label = null;

    if (geApiState.label?.id) {
        label = {
            id: geApiState.label?.id,
            name: geApiState.label?.name,
            description: geApiState.label?.tooltip,
        };
    }
    const eventData: AuditEvent = {
        id: uuidv4(),
        timestamp: new Date().toISOString(),
        type: action,
        trace_id: geApiState.auditTraceId,
    }

    const actorData: AuditActor = {
        app: {
            id: 'outlook_pep_addin',
            tenant_id: formatToUuid(geApiState.decodedTokenPayload?.tid),
            version: new GlobalConfiguration().getVersion(),
            type: 'eshare',
            organization_key: geApiState.extraServerAuditData?.organization?.key,
        },
        user: {
            email: geApiState.decodedTokenPayload?.smtp || geApiState.decodedTokenPayload?.upn || geApiState.decodedTokenPayload?.preferred_username,
            entra_user: {
                object_id: formatToUuid(geApiState.decodedTokenPayload?.oid),
                upn: geApiState.decodedTokenPayload?.upn,
                puid: geApiState.decodedTokenPayload?.puid,
                tenant_id: formatToUuid(geApiState.decodedTokenPayload?.tid),
            },
            organization_key: geApiState.extraServerAuditData?.organization?.key,
        }
    }

    const emailData: AuditingEmailData = {
        outlook_draft_id: geApiState.emailId,
        subject: geApiState.subject,
        sender: {
            email: geApiState.decodedTokenPayload?.smtp || geApiState.decodedTokenPayload?.upn || geApiState.decodedTokenPayload?.preferred_username,
        },
        // For these events we need allowed-only recipients in the email data.
        recipients: action === AuditEvents.EMAIL_SENT || action === AuditEvents.USER_DECISION
            ? allowedRecipients
            : allRecipients,
        ...(label && { sensitivity_label: label }),
        attachments: geApiState.attachments,
        native_links: geApiState.nativeLinks?.map((link) => {
            return {
                url: link
            }
        })
    }

    let pdpDecisionData: PDPDecision;

    if (action === AuditEvents.PDP_DECISION) {
        pdpDecisionData = {
            outcome: geApiState.allRecipients.map((recipient) => {
                return {
                    email_recipient: {
                        email: recipient.email,
                        is_cc: recipient.is_cc,
                        is_bcc: recipient.is_bcc
                    },
                    reason: recipient.reason,
                    sensitivity_label: label,
                    sensitivity_label_source: "email",
                    allowed: recipient.allowed
                }
            }),
            ...(geApiState.extraServerAuditData?.abac && geApiState.extraServerAuditData?.abac.type && {
                pdp_service: {
                    type: geApiState.extraServerAuditData?.abac?.type,
                    host: geApiState.extraServerAuditData?.abac?.host,
                    endpoint: geApiState.extraServerAuditData?.abac?.endpoint
                }
            })
        }
    }

    let user_decision: UserDecision[] | undefined;
    if (action === AuditEvents.USER_DECISION) {
        user_decision = geApiState.allRecipients && geApiState.allRecipients.length > 0 ? (
            geApiState.allRecipients
                // Filter out recipients without a decision, they are allowed from the beginning.
                .filter((recipient) => recipient.decision)
                .map((recipient) => {
                    return {
                        decision: recipient.decision,
                        recipient: {
                            email: recipient.email,
                            is_cc: recipient.is_cc,
                            is_bcc: recipient.is_bcc,
                        },
                        // If the decision is `override_removal`, we need to include the justification as "reason".
                        ...(recipient.decision === DECISION_OVERRIDE && { reason: recipient.justification })
                    }
            }
        )) : undefined;
    }

    return {
        outlook: {
            platform: platformData,
            event: eventData,
            actor: actorData,
            email: emailData,
            ...(pdpDecisionData && { pdp_decision: pdpDecisionData }),
            ...(user_decision && { outlook_user_decision: user_decision })
        }
    }
}

// check_recipients HTML-escapes email addresses in its response (e.g. "o'malley" -> "o&#x27;malley"),
// so a blocked recipient's email must be decoded before comparing it against the plain address on the
// compose item, otherwise the removal filter silently fails to match and the recipient stays on the send.
export const decodeHtmlEntities = (str: string): string => {
    if (!str) return str;
    const textarea = document.createElement('textarea');
    textarea.innerHTML = str;
    return textarea.value;
};

export const formatToUuid = (str: string): string | undefined => {
    if (!str) return undefined;
    const uuidRegex = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;
    if (uuidRegex.test(str)) {
        return str;
    }

    const canBeTransformedToUuidRegex = /^[0-9a-fA-F]{32}$/;
    if (canBeTransformedToUuidRegex.test(str)) {
        return `${str.slice(0, 8)}-${str.slice(8, 12)}-${str.slice(12, 16)}-${str.slice(16, 20)}-${str.slice(20)}`;
    }

    throw new Error("Could not format to UUID");
}

// Output platform in a user-friendly format.
export const getOutlookPlatform = ():string => {
    const platform = Office.context.platform;
    let platformDescription: string;

    switch (platform) {
        case Office.PlatformType.PC:
            platformDescription = "windows";
            break;
        case Office.PlatformType.Mac:
            platformDescription = "mac";
            break;
        case Office.PlatformType.OfficeOnline:
            platformDescription = "web";
            break;
        case Office.PlatformType.iOS:
            platformDescription = "ios";
            break;
        case Office.PlatformType.Android:
            platformDescription = "android";
            break;
        default:
            platformDescription = "unknown";
    }

    return platformDescription
}

export const endAPMValidationModalSpan = (error?: any) => {
    const span = (window as any).__currentModalSpan;
    if (span) {
        console.log('Closing the validation span');
        // End the span that we started before opening the iframe
        if (error) {
            span.addLabels({
                'error': error?.message,
                'error.type': error?.name
            });
        }
        span.end();
        delete (window as any).__currentModalSpan;
    }
}


export class ApiInstanceError extends Error {
    public apiInstance: GeApi;
    public originalError: any;

    constructor(originalError: any, apiInstance: GeApi) {
        super(originalError.message);
        this.apiInstance = apiInstance;
        this.originalError = originalError;
        this.name = 'ApiInstanceError';

        if (Error.captureStackTrace) {
            Error.captureStackTrace(this, ApiInstanceError);
        }
    }
}

// Checks if a feature is enabled in the i18n configuration.
// All enabled features are stored in the i18n translations under the "features" namespace.
// All features are enabled in development mode by default.
export const featureDetect = (feature: string, t: TFunction, i18n: i18n): boolean => {
    return (i18n.exists(`features.${feature}`) && !!t(`features.${feature}`, {
        returnObjects: true
    }));
}

// Transforms the tenant name to a user-friendly format.
// This is used to display the tenant name in the kibana UI for now.
// TODO: This should be removed when new manifest is released and the tenant names are correct in the manifest deployment url param.
export const transformTenant = (tenant?: string): string => {
    if (!tenant) {
        return 'Unknown';
    }
    if (tenant === 'eshare' || tenant === 'ge_prod_commercial') {
        return 'GE';
    }
    if (tenant === 'eshare_gov_com') {
        return 'eShare';
    }
    if (tenant === 'esg') {
        return 'ESG Demo';
    }

    return tenant;
};

// Tokens and their decoded claims must never be sent to APM. outlookToken and cwpToken are no longer
// part of GeApiState, but state saved by versions before AB#15755 can still contain them.
const APM_EXCLUDED_STATE_KEYS = ['outlookToken', 'cwpToken', 'decodedTokenPayload'];

// Returns the stored GeApiState (or the fallback) for APM context, without any token data.
export const getApmSafeState = (fallback?: GeApiState): Partial<GeApiState> | null => {
    const state: GeApiState | null = JSON.parse(localStorage.getItem("GeApiState")) || fallback || null;
    if (!state) {
        return null;
    }
    const safeState: Partial<GeApiState> = { ...state };
    APM_EXCLUDED_STATE_KEYS.forEach(key => delete (safeState as Record<string, unknown>)[key]);
    return safeState;
};
